import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from '../../../../providers/notification';

interface AdminRole {
    Id: string;
    Name: string;
    NormalizedName: string;
};

interface ComponentProps {
    handleClose: () => void;
}

const CreateRoleContent: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const postAdminRole = (data: AdminRole) => {
        return apiCall().post('/Administration/post/role/create', data);
    };

    const postAdminRoleMutation = useMutation(['post-admin-role'], postAdminRole, {retry: 0});

    const postAdminRoleHandler = async (values: FormikValues, resetForm: () => void) => {
        const adminRolePayload: AdminRole = {
            Id: 'string',
            Name: values?.name,
            NormalizedName: values?.name.toUpperCase(),
        };

        try {
            await postAdminRoleMutation.mutateAsync(adminRolePayload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Admin role has been created successfully',
                        type: 'success',
                    });
                    queryClient.refetchQueries(['get-all-role']);
                    handleClose();
                    resetForm();
                }
            })
        }catch (error: any) {
            showNotification({
                message: error?.response?.data?.Message || error?.message,
                type: 'error',
            });
        }
    }; 

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    })

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: '',
        },
        onSubmit: (values, { resetForm }) => {
            postAdminRoleHandler(values, resetForm)
        },
        validationSchema,
    });

    const handleCancel = () => {
        handleClose();
    }

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Role Type Name'
                        placeholder='Name'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleCancel} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postAdminRoleMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postAdminRoleMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateRoleContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

